import { Group, Stack } from '@mantine/core'
import { BidParamSpec } from '@pubstack/common/src/bidderCatalog'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Sizes } from '~/assets/style/tokens'
import { Title_P1 } from '~/components/_styled/titles'
import { Tag } from '~/components/Tag'
import { CreateSeatFormModel } from './CreateSeatStepper'
import { isDeclaredPlacementComplete } from './DeclaredPlacementsSection'
import { isPlacementMappedMethod, seatMappingTypeLabel } from './seatSetupOption'

type Props = {
  values: CreateSeatFormModel
  bidderDisplayName: string
  methodTitle: string
  bidParamSpecs: BidParamSpec[]
}

export const StepReview: FunctionComponent<Props> = ({ values, bidderDisplayName, methodTitle, bidParamSpecs }) => {
  const isSparkForPub = values.setupMethod === 'spark-for-pubs'
  // Placement-mapped seats never asked the mapping question, so reviewing the untouched form default
  // would describe a configuration the seat will not have.
  const isPlacementMapped = !!values.setupMethod && isPlacementMappedMethod(values.setupMethod)
  const declaredPlacements = isSparkForPub ? values.declaredPlacements.filter((row) => isDeclaredPlacementComplete(row, bidParamSpecs)) : []

  return (
    <Stack gap={Sizes[16]}>
      <Title_P1>Review</Title_P1>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 [&>dd]:m-0 [&>dd]:text-base [&>dd]:leading-[1.625rem] [&>dd]:text-jet [&>dt]:text-sm [&>dt]:leading-[1.375rem] [&>dt]:text-hurricane">
        <dt>Integration</dt>
        <dd>{bidderDisplayName}</dd>
        <dt>Type</dt>
        <dd>{methodTitle}</dd>
        <dt>Seat name</dt>
        <dd>{values.name}</dd>
        {!isPlacementMapped && (
          <>
            <dt>Mapping</dt>
            <dd>{seatMappingTypeLabel(values.mappingType)}</dd>
          </>
        )}
        {!isSparkForPub && (
          <>
            <dt>Bid adjustment</dt>
            <dd>{values.bidAdjustment.enable ? `${values.bidAdjustment.adjustment}%` : 'Disabled'}</dd>
          </>
        )}
      </dl>
      {isSparkForPub && (
        <Stack gap={Sizes[10]}>
          <Title_P1>Placement IDs</Title_P1>
          {declaredPlacements.map((row) => (
            <Stack key={row.placementId} gap={Sizes[4]}>
              <span className="font-roboto text-sm leading-[1.375rem] text-hurricane">{row.placementId}</span>
              <Group gap={Sizes[4]}>
                {bidParamSpecs
                  .filter((spec) => !!row.bidParams[spec.name]?.trim())
                  .map((spec) => (
                    <Tag key={spec.name} color={Colors.Jet}>
                      {spec.name} - {row.bidParams[spec.name]}
                    </Tag>
                  ))}
              </Group>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
